﻿/**
 * main.js
 * Main entry point for product configurator
 * 
 * Architecture:
 * - Core: State management, utilities, side management
 * - UI: Form handlers, pricing, helpers
 * - Images: Upload, processing, overlay
 * - Design: Color detection, edit mode, CSS rendering
 * - Cart: Data collection, save/load, preview
 * - Persistence: Serialization, validation, restoration
 * - Transform: Position/rotation management
 * 
 * Refactored: 2025-11-20
 * - Removed ~500 lines of dead code
 * - Consolidated state initialization
 * - Extracted side management to dedicated module
 * - Moved debug utilities to separate file
 * - Moved external app guards to separate file
 */

const __pcDebugEnabled = (() => {
  try {
    const params = new URLSearchParams(window.location.search || '');
    const v = params.get('debug') || params.get('dbg');
    return v === '1' || v === 'true';
  } catch (_) {
    return false;
  }
})();

function pcDebug(...args) {
  if (__pcDebugEnabled) {
    console.debug(...args);
  }
}

pcDebug('[PRODUCT-CORE-MAIN] File loaded successfully');

// Initialize external app guards (FastBundle, etc.)
// Cache-busted import ensures local preview picks up latest guard logic immediately.
import { initializeExternalAppGuards } from './product-core-external-app-guards.js';
initializeExternalAppGuards();

// ============================================
// Core Module Imports
// ============================================
import * as State from './product-core-state.js';
import './product-core-window-functions.js';
import './product-ui-button-sync-manager.js';
import './product-images-multi-image-upload.js';
import { createUploadSlot, updateMultiImageUI } from './product-images-multi-image-ui.js';

// ============================================
// Feature Module Imports
// ============================================
import * as ImageUpload from './product-images-image-upload.js';
import * as ImageLoader from './product-images-image-loader.js';
import * as ImageOverlay from './product-images-image-overlay.js';
import * as FormHandlers from './product-ui-form-handlers.js';
import * as UIHelpers from './product-ui-ui-helpers.js';
import { registerConfiguratorGlobals } from './product-core-globals-registry.js';
import { initScrollLock } from './product-scroll-lock.js';

// ============================================
// Cart Dependencies
// ============================================
import './product-cart-cart-data-collector.js';
import './product-cart-cart-save-service.js';
import * as PricingCalculator from './product-ui-pricing-calculator.js';

// ============================================
// Upload Progress Indicator
// ============================================
import { uploadProgressIndicator } from './product-ui-upload-progress-indicator.js';

// ============================================
// Unified Cart Save System
// ============================================
import cartSaveService from './product-cart-cart-save-service.js';

let processUploadedFileFn = null;
let hasLoggedMissingProcessUploadedFile = false;

// ============================================
// State Initialization & Event Handling
// ============================================

/**
 * Initialize unified preview system
 */
async function initializeUnifiedPreview() {
  try {
    console.log(' UnifiedDesignPreview: Initializing...');
    
    // Initialize unified preview
    const unifiedPreviewModule = await ensureUnifiedPreviewLoaded();
    if (unifiedPreviewModule && typeof unifiedPreviewModule.initialize === 'function') {
      await unifiedPreviewModule.initialize();
      if (typeof window !== 'undefined' && !window.unifiedPreview) {
        window.unifiedPreview = unifiedPreviewModule;
      }
    } else if (typeof unifiedPreviewModule === 'function') {
      const productWrapper = document.getElementById('product-wrapper');
      if (!productWrapper) {
        console.warn('Unified preview skipped: #product-wrapper not found');
        return;
      }

      let previewContainer = document.getElementById('unified-preview-container-product');
      if (!previewContainer) {
        previewContainer = document.createElement('div');
        previewContainer.id = 'unified-preview-container-product';
        previewContainer.style.cssText = [
          'position:absolute',
          'top:0',
          'left:0',
          'width:100%',
          'height:100%',
          'pointer-events:none',
          'z-index:10'
        ].join(';');
        productWrapper.appendChild(previewContainer);
      }

      const previewInstance = new unifiedPreviewModule({
        container: previewContainer,
        mode: 'INTERACTIVE',
        productConfig: window.productConfig
      });
      await previewInstance.initialize();
      if (typeof window !== 'undefined') {
        window.unifiedPreview = previewInstance;
      }
    } else {
      throw new Error('UnifiedDesignPreview export is not callable');
    }
    
    console.log(' Unified preview system initialized for product page');

  } catch (error) {
    console.error(' Failed to initialize unified preview:', error);
    // Don't fail the entire app if preview fails
  }
}

// ============================================
// Generic Lazy Loader Utility
// Prevents duplicate in-flight dynamic imports and standardizes export resolution
// ============================================
function createLazyLoader(options) {
  const { moduleRef, promiseRef, importFn, resolveFn, onError } = options;

  return async function ensureLoaded() {
    if (moduleRef.value) return moduleRef.value;
    if (promiseRef.value) return promiseRef.value;

    promiseRef.value = (async () => {
      try {
        const module = await importFn();
        const resolved = resolveFn(module);
        moduleRef.value = resolved;
        return resolved;
      } catch (error) {
        promiseRef.value = null;
        if (onError) onError(error);
        throw error;
      }
    })();

    return promiseRef.value;
  };
}

const _unifiedPreview = { value: null };
const _unifiedPreviewPromise = { value: null };
const ensureUnifiedPreviewLoaded = createLazyLoader({
  moduleRef: _unifiedPreview,
  promiseRef: _unifiedPreviewPromise,
  importFn: () => import('./product-transform-unified-design-preview.module.js'),
  resolveFn: (mod) => mod?.default || mod?.UnifiedDesignPreview || window?.UnifiedDesignPreview || null
});

async function waitForProductConfig(timeoutMs = 5000) {
  try {
    const existing = window.productConfig;
    if (existing && Array.isArray(existing.print_areas) && existing.print_areas.length > 0) {
      return existing;
    }

    return await new Promise((resolve) => {
      let timeoutId;
      const onReady = (e) => {
        cleanup();
        resolve(e?.detail?.config || window.productConfig || null);
      };
      const cleanup = () => {
        window.removeEventListener('productConfigReady', onReady);
        if (timeoutId) clearTimeout(timeoutId);
      };

      window.addEventListener('productConfigReady', onReady, { once: true });

      timeoutId = setTimeout(() => {
        cleanup();
        resolve(window.productConfig || null);
      }, timeoutMs);
    });
  } catch (e) {
    return window.productConfig || null;
  }
}

function hasPotentialEditModeFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search || '');
    let hasSession = false;
    try {
      hasSession = !!sessionStorage.getItem('edit_cart_item') || 
                   !!sessionStorage.getItem('reorder_from_order') || 
                   !!sessionStorage.getItem('revision_order_data');
    } catch (_) {}
    return (
      params.has('edit_cart_item') ||
      params.has('reorder_order_id') ||
      params.has('revision_order_id') ||
      params.has('design_id') ||
      hasSession
    );
  } catch (_) {
    return false;
  }
}

const _editModeOrchestrator = { value: null };
const _editModeOrchestratorPromise = { value: null };
const ensureEditModeModulesLoaded = createLazyLoader({
  moduleRef: _editModeOrchestrator,
  promiseRef: _editModeOrchestratorPromise,
  importFn: () =>
    Promise.all([
      import('./product-design-edit-mode-orchestrator.js'),
      import('./product-persistence-design-restoration-engine-v2.js'),
      import('./product-ui-button-state-manager-v2.js'),
      import('./product-state-restoration-state-manager.js'),
      import('./product-cart-cart-update-manager-v2.js')
    ]).then(([orchestratorModule]) => orchestratorModule),
  resolveFn: (mod) => mod?.default || window?.ProductConfigurator?.EditModeOrchestrator || null
});

const _customerMediaLibrary = { value: null };
const _customerMediaLibraryPromise = { value: null };
const ensureCustomerMediaLibraryLoaded = createLazyLoader({
  moduleRef: _customerMediaLibrary,
  promiseRef: _customerMediaLibraryPromise,
  importFn: () => import('./product-customer-media-library.js'),
  resolveFn: (mod) => mod?.customerMediaLibrary || window?.customerMediaLibrary || null
});

const _imageUploadService = { value: null };
const _imageUploadServicePromise = { value: null };
const ensureImageUploadServiceLoaded = createLazyLoader({
  moduleRef: _imageUploadService,
  promiseRef: _imageUploadServicePromise,
  importFn: () => import('./product-images-image-upload-service.js'),
  resolveFn: (mod) => mod?.default || window?.ProductConfigurator?.ImageUploadService || null
});

const _printAreaOverlayManager = { value: null };
const _printAreaOverlayManagerPromise = { value: null };
const ensurePrintAreaOverlayManagerLoaded = createLazyLoader({
  moduleRef: _printAreaOverlayManager,
  promiseRef: _printAreaOverlayManagerPromise,
  importFn: () => import('./product-design-print-area-overlay-manager.js'),
  resolveFn: (mod) => mod?.initializePrintAreaOverlay || window?.initializePrintAreaOverlay || null
});

const _cartPreviewPromise = { value: null };
const ensureAccurateCartPreviewLoaded = createLazyLoader({
  moduleRef: { value: true }, // no singleton to cache, just the promise itself
  promiseRef: _cartPreviewPromise,
  importFn: () => import('./product-cart-accurate-cart-preview.js'),
  resolveFn: () => true
});

function scheduleNonCriticalPreloads() {
  if (typeof window === 'undefined') return;

  const preload = () => {
    ensureCustomerMediaLibraryLoaded().catch(() => {});
    ensureImageUploadServiceLoaded().catch(() => {});
  };

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(preload, { timeout: 2500 });
  } else {
    setTimeout(preload, 1200);
  }
}

function resolveProcessUploadedFileFn() {
  if (typeof processUploadedFileFn === 'function') return processUploadedFileFn;

  const candidates = [
    ImageUpload?.processUploadedFile,
    window?.ImageUploadAPI?.processUploadedFile,
    window?.ImageUpload?.processUploadedFile,
    window?.processUploadedFile
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'function') {
      processUploadedFileFn = candidate;
      return processUploadedFileFn;
    }
  }

  if (!hasLoggedMissingProcessUploadedFile) {
    hasLoggedMissingProcessUploadedFile = true;
    console.error(
      'processUploadedFile is unavailable. Upload API bindings are missing in the current bundle/runtime.'
    );
  }

  return null;
}

async function buildFileFromLibraryUrl(url, filename) {
  const fallbackCandidates = (() => {
    try {
      const svc = window?.LibraryImageService;
      if (svc && typeof svc.buildStorageUrlCandidates === 'function') {
        const list = svc.buildStorageUrlCandidates(url);
        if (Array.isArray(list) && list.length) return list;
      }
    } catch (_) {}
    return [url];
  })();

  let lastStatus = null;
  let lastError = null;
  for (const candidate of fallbackCandidates) {
    try {
      const response = await fetch(candidate);
      if (!response.ok) {
        lastStatus = response.status;
        continue;
      }
      const blob = await response.blob();
      return new File([blob], filename, { type: blob.type });
    } catch (err) {
      lastError = err;
    }
  }

  if (lastError) {
    throw new Error(`Failed to fetch library image (${lastStatus || 'network'})`);
  }
  throw new Error(`Failed to fetch library image (${lastStatus || 404})`);
}

async function processLibrarySelectionImage(imageData) {
  const { url, filename, side, index } = imageData;
  const isFree = Boolean(imageData.isPaid || imageData.isFree || String(imageData.paymentStatus || '').toLowerCase() === 'paid');

  if (!url || !side || index === undefined || index === null) {
    console.warn('processLibraryImage received invalid payload, skipping:', imageData);
    return;
  }

  console.log(`Processing library image: ${filename} for ${side}[${index}]`);
  try {
    State.ReusedImageRegistry?.markReused?.(side, Number(index) || 0, {
      id: imageData.id || imageData.imageId || null,
      url,
      filename,
      mediaFingerprint: imageData.mediaFingerprint || imageData.media_fingerprint || null,
      paymentStatus: isFree ? 'paid' : (imageData.paymentStatus || imageData.payment_status || 'pending'),
      isPaid: isFree,
      badge: isFree ? 'Paid' : ''
    });
  } catch (_) {}
  const progressKey = uploadProgressIndicator.show(side, index, true);
  uploadProgressIndicator.setStep(progressKey, 'fetch', 'Loading selected image...');

  try {
    const file = await buildFileFromLibraryUrl(url, filename);
    uploadProgressIndicator.setStep(progressKey, 'validate', 'Preparing your design...');

    const processUploadedFile = resolveProcessUploadedFileFn();
    if (typeof processUploadedFile === 'function') {
      // PRIMARY PATH: processUploadedFile takes isFree + libraryUrl directly
      uploadProgressIndicator.setStep(progressKey, 'process', 'Applying design to product...');
      await processUploadedFile(file, side, index, isFree, url);
    } else {
      // FALLBACK: proceedWithUpload — MUST forward isFree=true and url as libraryUrl
      // so the image is never charged as a new upload.
      const proceedWithUploadFallback =
        ImageUpload?.proceedWithUpload ||
        window?.ImageUploadAPI?.proceedWithUpload ||
        window?.proceedWithUpload;

      if (typeof proceedWithUploadFallback === 'function') {
        uploadProgressIndicator.setStep(progressKey, 'process', 'Applying design to product...');
        // Signature: proceedWithUpload(event, side, index, file, isFree, libraryUrl)
        await proceedWithUploadFallback(
          { target: { files: [file] } },
          side,
          Number(index) || 0,
          file,
          isFree,
          url     // libraryUrl — the core fix
        );
      } else {
        // LAST RESORT FALLBACK: handleUpload — also forwards isFree + url
        const handleUploadFallback =
          ImageUpload?.handleUpload ||
          window?.ImageUploadAPI?.handleUpload ||
          window?.handleUpload;

        if (typeof handleUploadFallback !== 'function') {
          throw new Error('Library processing unavailable (processUploadedFile/handleUpload missing)');
        }

        file.__skipNonVectorWarning = true;
        window.__skipNonVectorWarningOnce = true;
        uploadProgressIndicator.setStep(progressKey, 'process', 'Applying design to product...');
        // Signature: handleUpload(event, side, index, isFree, libraryUrl)
        await handleUploadFallback(
          { target: { files: [file] } },
          side,
          Number(index) || 0,
          isFree,
          url    // libraryUrl
        );
      }
    }

    if (typeof window.updateMultiImageUI === 'function') {
      window.updateMultiImageUI(side);
    }

    // Dispatch uploadedLocationsChanged so PricingCalculator recalculates the fee.
    // The reusedImageRegistryChanged event (dispatched by the registry) also triggers
    // a recalc, but we dispatch this as a belt-and-suspenders approach and also
    // to fix the currently dead uploadedLocationsChanged listener in pricing-calculator.js.
    if (typeof document !== 'undefined') {
      try {
        const { ReusedImageRegistry } = await import('./product-core-state.js');
        document.dispatchEvent(new CustomEvent('uploadedLocationsChanged', {
          detail: { reusedCount: ReusedImageRegistry.getReusedCount() }
        }));
      } catch (_) {
        document.dispatchEvent(new CustomEvent('uploadedLocationsChanged', { detail: {} }));
      }
    }

    uploadProgressIndicator.complete(progressKey);
  } catch (error) {
    console.error('Error processing library image:', error);
    uploadProgressIndicator.updateStatus(progressKey, 'Failed to load image');
    setTimeout(() => uploadProgressIndicator.hide(progressKey), 1200);
  }
}

// ============================================
// Application Initialization
// ============================================

async function initializeApplication() {
  pcDebug('[PC-INIT] initializeApplication called', {
    path: window?.location?.pathname,
    readyState: document?.readyState,
    initState: window?.__configuratorInitState || 'unset'
  });

  if (typeof window !== 'undefined' && window.location?.pathname?.includes('/cart')) {
    try {
      await ensureAccurateCartPreviewLoaded();
      console.log('Cart page detected: loaded accurate cart preview module only');
    } catch (error) {
      console.warn('Failed to load accurate cart preview module:', error);
    }
    return;
  }

  if (typeof window !== 'undefined') {
    // Atomic init state machine: idle -> initializing -> ready|error
    const state = window.__configuratorInitState;
    if (state === 'initializing' || state === 'ready') {
      console.log(`⏭️ initializeApplication skipped (state: ${state})`);
      return;
    }
    window.__configuratorInitState = 'initializing';
    // Keep legacy booleans in sync for external consumers
    window.__productConfiguratorAppInitialized = true;
    window.__configuratorFullyInitialized = false;
  }

  console.log(' Initializing Product Configurator...');

  // Initialize scroll lock system
  initScrollLock();

  try {
    const overlayManagerPromise = ensurePrintAreaOverlayManagerLoaded().catch((error) => {
      console.warn('Failed to load print area overlay module:', error);
      return null;
    });

    const productConfig = await waitForProductConfig(5000);

    // Step 1: Initialize state (consolidated)
    console.log(' Initializing state...');
    window.dispatchEvent(new CustomEvent('configurator:progress', { detail: { step: 'state' } }));
    State.initializeState(productConfig, { source: 'core-init', allowConfigUpgrade: true });

    // Step 1.5: Initialize print area overlay (canvas + clip container)
    console.log('[DE DEBUG] Initializing print area overlay manager (v2026-04-23-2)...');
    try {
      const overlayInitializer = await overlayManagerPromise;
      if (typeof overlayInitializer === 'function') {
        const initialized = overlayInitializer();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('configurator:print-area-overlay-ready', {
            detail: {
              initialized: initialized === true,
              timestamp: Date.now(),
              source: 'product-core-main'
            }
          }));
        }
      }
    } catch (overlayError) {
      console.warn('⚠️ Failed to initialize print area overlay manager:', overlayError);
    }

    // Step 2: Check for edit mode (V2 system only)
    console.log(' Checking for edit mode...');
    let isEditModeActive = false;
    if (hasPotentialEditModeFromUrl()) {
      const orchestrator = await ensureEditModeModulesLoaded();
      if (!orchestrator || typeof orchestrator.initialize !== 'function') {
        throw new Error('EditModeOrchestrator not available - critical module missing');
      }
      await orchestrator.initialize();
      isEditModeActive = !!orchestrator?.isActive;
    } else {
      console.log('Standard product view detected - skipping edit-mode bundle load');
    }

    // Step 3: Initialize upload handlers
    console.log(' Initializing upload handlers...');
    window.dispatchEvent(new CustomEvent('configurator:progress', { detail: { step: 'uploads' } }));
    if (typeof ImageUpload.initializeUploadHandlers === 'function') {
      ImageUpload.initializeUploadHandlers();
    }

    // Step 4: Setup observers
    console.log('️ Setting up observers...');
    if (typeof ImageOverlay.setupProductImageObserver === 'function') {
      ImageOverlay.setupProductImageObserver();
    }

    // Step 5: Setup pricing calculator
    console.log(' Setting up pricing calculator...');
    if (typeof FormHandlers.setupTotalQuantityDistributionDebounced === 'function') {
      FormHandlers.setupTotalQuantityDistributionDebounced(50, 'core-init');
    } else if (typeof FormHandlers.setupTotalQuantityDistribution === 'function') {
      FormHandlers.setupTotalQuantityDistribution('core-init');
    }

    // Step 7: Initialize accordions
    console.log(' Initializing accordions...');
    if (typeof UIHelpers.enableAccordions === 'function') {
      UIHelpers.enableAccordions();
    }

    // Step 8: Set initial panel/button visibility based on whether images exist
    console.log('️ Setting initial panel visibility...');
    if (typeof UIHelpers.updatePanelVisibility === 'function') {
      UIHelpers.updatePanelVisibility();
    }

    // Step 9: Initialize unified preview system (only for non-edit modes)
    console.log(' Initializing unified preview system...');
    window.dispatchEvent(new CustomEvent('configurator:progress', { detail: { step: 'preview' } }));
    if (!isEditModeActive) {
      await initializeUnifiedPreview();
    }

    scheduleNonCriticalPreloads();

    if (typeof window !== 'undefined') {
      window.__configuratorInitState = 'ready';
      window.__configuratorFullyInitialized = true;
      window.dispatchEvent(new CustomEvent('configurator:progress', { detail: { step: 'ready' } }));
      window.dispatchEvent(new CustomEvent('configurator:ready', {
        detail: { timestamp: Date.now() }
      }));
    }

    console.log(' Product Configurator initialized successfully!');

  } catch (error) {
    if (typeof window !== 'undefined') {
      window.__configuratorInitState = 'error';
      window.__configuratorFullyInitialized = false;
    }
    console.error(' Failed to initialize Product Configurator:', error);
    console.error('Stack trace:', error.stack);
  }
}

// ============================================
// One-time bootstrap bindings
// ============================================
// If this module is injected more than once, avoid re-binding listeners and globals.
if (typeof window === 'undefined' || !window.__productConfiguratorBootstrapBound) {
  if (typeof window !== 'undefined') {
    window.__productConfiguratorBootstrapBound = true;
  }

  // ============================================
  // Auto-initialize when DOM is ready
  // ============================================
  if (document.readyState === 'loading') {
    pcDebug('[PC-BOOTSTRAP] binding DOMContentLoaded initializeApplication');
    document.addEventListener('DOMContentLoaded', initializeApplication);
  } else {
    // DOM already loaded
    pcDebug('[PC-BOOTSTRAP] calling initializeApplication immediately');
    initializeApplication();
  }

  // ============================================
  // Late productConfig: re-initialize handlers
  // ============================================
  // When the backend fetch takes longer than waitForProductConfig's timeout (5s),
  // initializeApplication runs with null config and finds 0 upload inputs.
  // configurator-integration.liquid renders upload sections AFTER the backend responds,
  // so we must re-run initializeUploadHandlers once productConfig is available.
  // Debounced late-config handler to prevent event storms
  let __productConfigReadyTimeout = null;
  window.addEventListener('productConfigReady', () => {
    if (__productConfigReadyTimeout) clearTimeout(__productConfigReadyTimeout);
    __productConfigReadyTimeout = setTimeout(() => {
      __productConfigReadyTimeout = null;
      // Gate: do NOT re-run handlers if main init is still in progress
      if (window.__configuratorInitState === 'initializing') {
        console.log('⏭️ productConfigReady ignored: main initialization still in progress');
        return;
      }
      if (typeof ImageUpload.initializeUploadHandlers === 'function') {
        const pending = document.querySelectorAll('.upload-input-pending');
        if (pending.length > 0) {
          ImageUpload.initializeUploadHandlers();
        }
      }
      // Also re-initialize state with real config if it came in late
      if (window.productConfig && typeof State.initializeState === 'function') {
        const alreadyHasAreas = Array.isArray(window.productConfig.print_areas) &&
          window.productConfig.print_areas.length > 0;
        if (alreadyHasAreas) {
          State.initializeState(window.productConfig, { source: 'productConfigReady', allowConfigUpgrade: true });
        }
      }
    }, 300);
  }, { once: true });

  // ============================================
  // Legacy Support: Export key functions globally
  // ============================================
  // These are exported globally to maintain compatibility with inline event handlers
  // in product.liquid (e.g., onclick="functionName()")

  registerConfiguratorGlobals({
    ImageUpload,
    ImageOverlay,
    ImageLoader,
    updateMultiImageUI,
    createUploadSlot
  });
} else {
  pcDebug('[PC-BOOTSTRAP] already bound, skipping duplicate bindings');
}

if (typeof window !== 'undefined') {
  
  // Enhanced triggerUpload that opens media library first
  // This allows customers to choose from their paid images (fee-free) or upload new (with fee)
  window.triggerUpload = async function(side, index = 0) {
    console.log(`️ triggerUpload called for ${side}[${index}]`);

    // Feature flag: only bypass modal when explicitly disabled.
    // Undefined should default to enabled so the media-library modal opens.
    if (window.__reuseV2 === false) {
      if (typeof ImageUpload.triggerUpload === 'function') {
        ImageUpload.triggerUpload(side, index);
      }
      return;
    }

    // Local preview: keep modal behavior enabled for debugging/media-library QA.
    // Allow explicit opt-out when needed.
    const isLocalPreview = /^(127\.0\.0\.1|localhost)$/i.test(window.location?.hostname || '');
    if (isLocalPreview && window.__forceDirectUploadOnLocal === true) {
      console.log('Local preview detected and __forceDirectUploadOnLocal=true - using direct upload input');
      if (typeof ImageUpload.triggerUpload === 'function') {
        ImageUpload.triggerUpload(side, index);
      }
      return;
    }
    if (isLocalPreview) {
      console.log('Local preview detected - opening media library modal (local bypass disabled)');
    }
    
    // MEDIA LIBRARY DISABLED
    // The saved-designs media library existed to let customers re-use artwork they
    // had already paid setup on. Setup fee is now a flat once-per-cart charge, so
    // the library has no pricing role and is hidden for now.
    //
    // Nothing is deleted: the module, its service and CSS are all intact, and
    // uploads still sync into the library in the background (cart-save-service
    // syncCurrentUploadsToCustomerMediaLibrary) so saved designs keep accumulating
    // and will be there when this is switched back on.
    //
    // Re-enable by setting window.DE_MEDIA_LIBRARY_ENABLED = true
    // (see snippets/configurator-integration.liquid).
    if (window.DE_MEDIA_LIBRARY_ENABLED !== true) {
      console.log('Media library disabled - opening file picker directly');
      if (typeof ImageUpload.triggerUpload === 'function') {
        ImageUpload.triggerUpload(side, index);
      }
      return;
    }

    // Check if we're on mobile - use mobile upload panel instead of desktop modal
    if (window.mobileUpload && window.mobileUpload.isMobile) {
      console.log(` Mobile detected - using mobile upload panel for ${side}[${index}]`);
      window.mobileUpload.handleMobileUpload(side, null, index);
      return;
    }
    
    console.log(`️ Desktop detected - opening media library modal`);
    
    let mediaLibraryLoadIndicatorKey = null;
    const mediaLibraryLoadIndicatorTimeout = setTimeout(() => {
      mediaLibraryLoadIndicatorKey = uploadProgressIndicator.show(side, index, true);
      uploadProgressIndicator.setStep(
        mediaLibraryLoadIndicatorKey,
        'fetch',
        'Opening your media library...'
      );
    }, 180);

    let mediaLibrary = null;
    try {
      mediaLibrary = await ensureCustomerMediaLibraryLoaded();
    } catch (error) {
      console.warn('Media library failed to load (dynamic import/init), falling back to direct upload input:', error);
      if (typeof ImageUpload.triggerUpload === 'function') {
        ImageUpload.triggerUpload(side, index);
      }
      return;
    } finally {
      clearTimeout(mediaLibraryLoadIndicatorTimeout);
      if (mediaLibraryLoadIndicatorKey) {
        uploadProgressIndicator.hide(mediaLibraryLoadIndicatorKey);
      }
    }
    if (!mediaLibrary || typeof mediaLibrary.open !== 'function') {
      console.warn('Media library unavailable (open() missing), falling back to direct upload input');
      if (typeof ImageUpload.triggerUpload === 'function') {
        ImageUpload.triggerUpload(side, index);
      }
      return;
    }

    // Desktop: Open media library with callback for when image is selected.
    // The modal's confirmSelection() already calls applySelection() (registry + tracking +
    // canonical event) before invoking this callback. We only need to run the pipeline here.
    try {
      mediaLibrary.open(side, index, (selectedImage) => {
        console.log(' Image selected from media library:', selectedImage);

        if (selectedImage && selectedImage.url) {
          if (typeof window.processLibraryImage === 'function') {
            window.processLibraryImage(selectedImage);
          }
        }
      });
    } catch (error) {
      console.warn('Media library failed during open(), falling back to direct upload input:', error);
      if (typeof ImageUpload.triggerUpload === 'function') {
        ImageUpload.triggerUpload(side, index);
      }
    }
  };
  
  // Original triggerUpload for direct file input (bypasses media library)
  window.triggerUploadDirect = ImageUpload.triggerUpload;
  
  // Process image selected from media library
  window.processLibraryImage = processLibrarySelectionImage;

  // Bridge "Upload New" from media-library modal into the standard upload pipeline.
  // The modal dispatches `mediaLibraryUploadNew` with { file, side, index }.
  if (!window.__mediaLibraryUploadNewListenerBound) {
    window.__mediaLibraryUploadNewListenerBound = true;
    document.addEventListener('mediaLibraryUploadNew', async (event) => {
      const detail = event?.detail || {};
      const file = detail.file;
      const side = detail.side;
      const index = Number.isFinite(Number(detail.index)) ? Number(detail.index) : 0;

      if (!file || !side) {
        console.warn('mediaLibraryUploadNew ignored: missing file or side', detail);
        return;
      }

      try {
        if (typeof ImageUpload.handleUpload !== 'function') {
          throw new Error('ImageUpload.handleUpload is unavailable');
        }

        console.log(`Routing mediaLibraryUploadNew to handleUpload for ${side}[${index}]`);
        await ImageUpload.handleUpload({ target: { files: [file] } }, side, index);
      } catch (error) {
        console.error('Failed to process mediaLibraryUploadNew via handleUpload:', error);
      }
    });
  }
}
